import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LifeBuoy, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingState, ErrorState, EmptyState } from "@/components/shared/States";
import { PageHeader, SectionCard } from "@/components/shared/PageChrome";
import { AccessGuide } from "@/components/shared/AccessGuide";
import { RepairReportForm, RepairCard } from "@/components/client/RepairReport";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const INTENTS = {
  "new-request": { title: "New accommodation request", hint: "Tell us where, when and for how many people. A Jigsaw coordinator will open the request and confirm the reference with you.", template: "New accommodation request\nLocation:\nArrival date:\nDeparture date:\nRooms / people:\nAccessibility or other needs:\n" },
  "booking-change": { title: "Request a booking change", hint: "Quote the booking reference and describe the extension, cancellation or amendment you need.", template: "Booking change request\nBooking reference:\nChange needed:\n" },
  "guest-update": { title: "Update guest information", hint: "Tell us which person and what needs to change. Jigsaw staff apply the update and confirm back here.", template: "Guest information update\nGuest name:\nBooking reference (if known):\nWhat needs to change:\n" },
};

export default function ClientSupport() {
  const { memberships } = useAuth();
  const clientOrgs = memberships.filter((m) => m.org_type === "CLIENT");
  const [orgId, setOrgId] = useState(clientOrgs[0]?.organisation_id || "");
  const orgParams = clientOrgs.length > 1 ? { organisation_id: orgId } : {};
  const gen = useRef(0); // request-generation guard: a late response for the previous organisation is discarded
  const switchOrg = (id) => { gen.current += 1; setOrgId(id); setItems(null); setMessage(""); setError(false); };
  const [params] = useSearchParams();
  const intent = INTENTS[params.get("intent")];
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState(() => (intent ? intent.template : ""));
  const [submitting, setSubmitting] = useState(false);

  const load = () => { const g = gen.current; return api.get("/client/support", { params: orgParams }).then((r) => {
    if (g !== gen.current) return; // stale response for a previously selected organisation
    setItems(r.data.items);
    // Explicit read: visiting this page IS the client opening/reading their thread.
    api.post("/client/support/read", null, { params: orgParams }).then(() => window.dispatchEvent(new Event("jigsaw:support-read"))).catch(() => {});
  }).catch(() => setError(true)); };

  useEffect(() => { load(); }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!message.trim()) return toast.error("Please enter a message before sending.");
    setSubmitting(true);
    try {
      await api.post("/client/support", { body: message, ...orgParams });
      setMessage("");
      toast.success("Message sent to the Jigsaw team.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send this message.");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) return <ErrorState testId="client-support-error" />;
  if (!items) return <LoadingState testId="client-support-loading" />;

  return (
    <div data-testid="client-support-page" className="space-y-5">
      <PageHeader title="Support" subtitle="Send a message to the Jigsaw team about any stay, document or request. Messages are reviewed by our staff - there is no live chat or automated reply in this Alpha." />
      {clientOrgs.length > 1 && (
        <div data-testid="support-org-switcher" className="flex flex-wrap items-center gap-2 rounded-[10px] border border-[#BFD3F5] bg-[#E8F0FC] px-4 py-2 text-[13px] text-[#1F4E9C]">
          <span className="font-medium">Messaging as</span>
          <select data-testid="support-org-select" aria-label="Organisation this conversation belongs to" className="h-8 rounded border border-[#BFD3F5] bg-white px-2 text-[13px] text-jigsaw-navy" value={orgId} onChange={(e) => switchOrg(e.target.value)}>
            {clientOrgs.map((m) => <option key={m.organisation_id} value={m.organisation_id}>{m.organisation_name}</option>)}
          </select>
          <span className="text-[12px]">— each organisation has its own conversation with Jigsaw</span>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <RepairReportForm key={orgId} onSubmitted={load} />
          <SectionCard testId="client-support-compose" title={intent ? intent.title : "New message"} subtitle={intent?.hint}>
            <div className="space-y-3">
              <Textarea data-testid="client-support-message-input" rows={intent ? 8 : 4} placeholder="Describe your question or issue..." value={message} onChange={(e) => setMessage(e.target.value)} />
              <Button data-testid="client-support-send-button" onClick={submit} disabled={submitting}>
                <Send className="mr-1.5 h-3.5 w-3.5" /> {submitting ? "Sending..." : "Send message"}
              </Button>
            </div>
          </SectionCard>

          <div>
            <h2 className="mb-3 text-base font-semibold text-jigsaw-navy">Conversation</h2>
            {items.length === 0 ? (
              <EmptyState testId="client-support-empty" icon={LifeBuoy} title="No messages yet" description="Anything you send will appear here." />
            ) : (
              <div className="space-y-2">
                {items.map((n) => n.kind === "REPAIR_REPORT" ? <RepairCard key={n.id} note={n} /> : (
                  <div key={n.id} data-testid={`client-support-message-${n.id}`} className={`rounded-[10px] border p-4 ${n.is_staff_reply ? "border-jigsaw-orange/30 bg-jigsaw-orange/5" : "border-jigsaw-border bg-white"}`}>
                    <p className="text-xs font-medium text-jigsaw-muted">{n.is_staff_reply ? (n.author_name || "Jigsaw team") : "You"}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-jigsaw-navy">{n.body}</p>
                    <p className="mt-2 text-xs text-jigsaw-muted">{new Date(n.created_at).toLocaleString("en-GB")}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <SectionCard testId="client-support-how" title="How support works" className="self-start">
          <ol className="space-y-3 text-[13px] text-jigsaw-navy">
            <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-jigsaw-navy text-[12px] font-semibold text-white">1</span>Your message lands in the Jigsaw Support Inbox and is visible to the coordinators for your organisation.</li>
            <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-jigsaw-navy text-[12px] font-semibold text-white">2</span>A staff member replies in this thread; the bell in the top bar shows unread replies.</li>
            <li className="flex gap-3"><span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-jigsaw-navy text-[12px] font-semibold text-white">3</span>New requests, booking changes and guest updates are actioned by staff and appear in Requests, Bookings and People once recorded.</li>
          </ol>
        </SectionCard>
      </div>
      <AccessGuide workspace="CLIENT" />
    </div>
  );
}
